import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type SalaryPolicyRuleRecord = {
  id?: string;
  ruleId: string;
  ruleKey: string;
  ruleValue: string;
  ruleType: string;
  description: string;
};

export interface SalaryPolicyRecord {
  id: string;
  name: string;
  isActive: boolean;
  rules: SalaryPolicyRuleRecord[];
  createdAt: string;
  updatedAt: string;
}

export type SalaryRuleCatalogItem = {
  id: string;
  code: string;
  title: string;
  description: string;
  type: "salary";
  global_value: unknown;
  data_type: string;
};

export type SalaryPolicyLinkedRuleApi = {
  id?: string | number;
  rule_id?: string | number;
  rule_key?: string;
  rule_value?: string;
  rule_type?: string;
  data_type?: string;
  description?: string | null;
  code?: string;
  title?: string;
};

export type SalaryPolicyDraft = {
  name: string;
  isActive: boolean;
};

const LS_KEY = "inventory.salaryPolicies.v1";

type SalaryPolicyApiRecord = {
  id?: number | string;
  name?: string | null;
  is_active?: boolean | null;
  rules?: unknown;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertApiResponse = { message?: string; data?: SalaryPolicyApiRecord };
type DeleteApiResponse = { message?: string };

function dedupeById(items: SalaryPolicyRecord[]): SalaryPolicyRecord[] {
  const byId = new Map<ID, SalaryPolicyRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function extractRowsFromResponse(body: unknown): SalaryPolicyApiRecord[] {
  if (Array.isArray(body)) return body as SalaryPolicyApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const fromBlock = (block: unknown): SalaryPolicyApiRecord[] | null => {
    if (Array.isArray(block)) return block as SalaryPolicyApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as SalaryPolicyApiRecord[];
    }
    return null;
  };
  for (const key of ["data", "salary_policies", "items", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractFromUpsertBody(body: unknown): SalaryPolicyApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const block = data as Record<string, unknown>;
    if ("id" in block || typeof block.name === "string") {
      return block as unknown as SalaryPolicyApiRecord;
    }
  }
  if ("id" in root && typeof root.name === "string") {
    return root as unknown as SalaryPolicyApiRecord;
  }
  return null;
}

function mapRules(raw: unknown): SalaryPolicyRuleRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row) => row && typeof row === "object")
    .map((row) => {
      const r = row as Record<string, unknown>;
      return {
        id: r.id != null ? str(r.id) : undefined,
        ruleId: r.rule_id != null ? str(r.rule_id) : "",
        ruleKey: str(r.code ?? r.rule_key),
        ruleValue: str(r.rule_value),
        ruleType: str(r.rule_type) || "salary",
        description: r.description != null ? str(r.description) : "",
      };
    })
    .filter((r) => r.ruleId !== "");
}

function mapApiRecord(record: SalaryPolicyApiRecord): SalaryPolicyRecord {
  const created = record.created_at || new Date().toISOString();
  const updated = record.updated_at || created;
  return {
    id: String(record.id ?? ""),
    name: str(record.name),
    isActive: record.is_active !== false,
    rules: mapRules(record.rules),
    createdAt: created,
    updatedAt: updated,
  };
}

function buildApiPayload(draft: SalaryPolicyDraft) {
  return {
    name: draft.name.trim(),
    is_active: draft.isActive,
  };
}

function load(): SalaryPolicyRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SalaryPolicyRecord[];
    if (!Array.isArray(parsed)) return [];
    return dedupeById(
      parsed.map((r) => ({
        ...r,
        rules: Array.isArray(r.rules) ? r.rules : [],
      })),
    );
  } catch {
    return [];
  }
}

function save(items: SalaryPolicyRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

const PAGE_SIZE = 20;

export function salaryPolicyApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export function listSalaryPolicies(): SalaryPolicyRecord[] {
  return load().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getSalaryPolicyById(id: ID): SalaryPolicyRecord | undefined {
  return load().find((p) => p.id === id);
}

export async function syncSalaryPoliciesFromApi(): Promise<SalaryPolicyRecord[]> {
  try {
    const collected: SalaryPolicyApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/salary-policies", {
        params: { page, per_page: PAGE_SIZE },
      });
      collected.push(...extractRowsFromResponse(response.data));
      lastPage = readLastPageFromListBody(response.data) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
    save(records);
    return listSalaryPolicies();
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 403) {
      throw error;
    }
    console.error("[SalaryPolicies] sync failed", error);
    return listSalaryPolicies();
  }
}

export async function createSalaryPolicy(
  draft: SalaryPolicyDraft,
): Promise<{ record: SalaryPolicyRecord; message: string }> {
  const items = load();
  const response = await api.post<UpsertApiResponse>("/api/v1/salary-policies", buildApiPayload(draft));
  const created = extractFromUpsertBody(response.data);
  if (!created || created.id == null) {
    throw new Error("Invalid create salary policy response.");
  }
  const record = mapApiRecord(created);
  const idx = items.findIndex((p) => p.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Salary policy created successfully." };
}

export async function updateSalaryPolicy(
  id: ID,
  draft: SalaryPolicyDraft,
): Promise<{ record: SalaryPolicyRecord; message: string } | null> {
  const items = load();
  const idx = items.findIndex((p) => p.id === id);
  if (idx === -1) return null;
  const response = await api.put<UpsertApiResponse>(`/api/v1/salary-policies/${id}`, buildApiPayload(draft));
  const updated = extractFromUpsertBody(response.data);
  if (!updated) {
    throw new Error("Invalid update salary policy response.");
  }
  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Salary policy updated successfully." };
}

export async function deleteSalaryPolicy(id: ID): Promise<{ success: boolean; message: string }> {
  const response = await api.delete<DeleteApiResponse>(`/api/v1/salary-policies/${id}`);
  const items = load();
  const next = items.filter((p) => p.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return {
    success: removed,
    message: response.data?.message || "Salary policy deleted successfully.",
  };
}

function parseSalaryRulesSetupBody(body: unknown): {
  catalog: SalaryRuleCatalogItem[];
  linked: SalaryPolicyLinkedRuleApi[];
} {
  const block = body && typeof body === "object" && "data" in body ? (body as { data?: unknown }).data : body;
  const b = block && typeof block === "object" ? (block as Record<string, unknown>) : {};
  const catalogRaw = Array.isArray(b.catalog) ? b.catalog : [];
  const linkedRaw = Array.isArray(b.linked) ? b.linked : [];
  const catalog: SalaryRuleCatalogItem[] = catalogRaw.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      id: str(r.id),
      code: str(r.code),
      title: str(r.title),
      description: str(r.description),
      type: "salary",
      global_value: r.global_value,
      data_type: str(r.data_type) || "string",
    };
  });
  return { catalog, linked: linkedRaw as SalaryPolicyLinkedRuleApi[] };
}

export async function fetchSalaryRulesCatalog(): Promise<{
  catalog: SalaryRuleCatalogItem[];
  linked: SalaryPolicyLinkedRuleApi[];
}> {
  const response = await api.get(`/api/v1/salary-policies/salary-rules-catalog`);
  return parseSalaryRulesSetupBody(response.data);
}

export async function fetchSalaryRulesSetup(policyId: ID): Promise<{
  catalog: SalaryRuleCatalogItem[];
  linked: SalaryPolicyLinkedRuleApi[];
}> {
  const response = await api.get(`/api/v1/salary-policies/${policyId}/salary-rules-setup`);
  return parseSalaryRulesSetupBody(response.data);
}

export async function fetchAssignableSalaryPoliciesForEmployees(): Promise<Array<{ id: string; name: string }>> {
  const response = await api.get<{ data?: Array<{ id?: number; name?: string }> }>(
    "/api/v1/salary-policies/assignable-for-employees",
  );
  const rows = Array.isArray(response.data?.data) ? response.data.data : [];
  return rows
    .map((r) => ({ id: String(r.id ?? ""), name: String(r.name ?? "").trim() }))
    .filter((r) => r.id !== "" && r.name !== "");
}

export async function syncSalaryPolicyRules(
  policyId: ID,
  rules: Array<{ rule_id: number | string; rule_value: string; rule_type?: string; description?: string }>,
): Promise<{ message: string }> {
  const response = await api.put<{ message?: string; data?: unknown }>(
    `/api/v1/salary-policies/${policyId}/salary-rules`,
    { rules },
  );
  await syncSalaryPoliciesFromApi();
  return { message: str(response.data?.message) || "Salary policy rules saved." };
}
